import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Globe, Users, BookOpen, GraduationCap, Building2, ArrowRight, ChevronLeft, ChevronRight, Languages, Lightbulb, HandHeart } from "lucide-react"
import { Button } from "../components/ui/button"
import heroIntl from "../assets/internationauxStudent/WhatsApp Image 2026-03-07 at 14.50.48.jpeg"
import groupIntl from "../assets/internationauxStudent/WhatsApp Image 2026-03-07 at 14.52.44.jpeg"
import logoCecongo from "../assets/internationauxStudent/logo.jpeg"

const komeraStudents = [
    "/students/1.JPG",
    "/students/2.JPG",
    "/students/3.JPG",
    "/students/4.JPG",
    "/students/5.JPG",
    "/students/6.JPG",
    "/students/7.JPG"
]

const komeraProfessors = [
    "/prof/1.JPG",
    "/prof/2.jpg",
    "/prof/3.JPG",
    "/prof/4.jpeg",
    "/prof/5.JPG"
]

export default function EtudiantsInternationaux() {
    const navigate = useNavigate()
    const stats = [
        { label: "Nationalités", value: "15+", icon: Globe },
        { label: "Étudiants", value: "120+", icon: Users },
        { label: "Bourses", value: "25", icon: GraduationCap },
        { label: "Partenariats", value: "30+", icon: Building2 }
    ]

    return (
        <div className="bg-background">
            {/* Hero Section */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src={heroIntl} alt="Étudiants internationaux congolais sur le campus de l'UNG" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/75 to-primary/40" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Bienvenue à l'UNG</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Étudiants</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Étudiants</span>
                            <span className="block text-white mt-2">Internationaux</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Rejoignez une communauté académique vibrante et diversifiée. L'UNG accueille des étudiants du monde entier et s'engage à vous offrir une expérience éducative exceptionnelle.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button
                                onClick={() => navigate("/admission")}
                                className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl"
                            >
                                Candidater
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => navigate("/etudiants/blog")}
                                className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm"
                            >
                                En savoir plus
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar */}
            <section className="bg-secondary/5 border-b border-border">
                <div className="container py-8 flex flex-wrap items-center justify-between gap-12">
                    {stats.map((stat, idx) => {
                        const Icon = stat.icon
                        return (
                            <div key={idx} className="flex items-center gap-4 group">
                                <div className="p-3 bg-primary/5 rounded-xl group-hover:bg-accent/10 transition-colors">
                                    <Icon className="w-6 h-6 text-primary group-hover:text-accent transition-colors" />
                                </div>
                                <div>
                                    <div className="text-2xl font-black text-primary leading-none mb-1">{stat.value}</div>
                                    <div className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
                        <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
                        <span>/</span>
                        <span className="text-foreground font-medium">Étudiants Internationaux</span>
                    </div>
                    {/* Presentation with Image */}
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
                        <div className="relative">
                            <div className="aspect-[4/3] overflow-hidden border-2 border-border relative">
                                <img src={groupIntl} alt="Rassemblement des étudiants congolais de l'UNG" className="w-full h-full object-cover" />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-card p-4 border-2 border-border flex items-center gap-4">
                                <img src={logoCecongo} alt="Logo C.E.CO.NGO" className="w-12 h-12 rounded-full object-cover border border-border" />
                                <div>
                                    <p className="text-sm font-bold text-foreground">C.E.CO.NGO</p>
                                    <p className="text-xs text-muted-foreground">Communauté congolaise</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <h2 className="text-3xl font-bold text-foreground">Pourquoi choisir l'UNG ?</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                L'Université de Ngozi est reconnue pour son ouverture internationale et ses programmes de recherche innovants.
                                En tant qu'étudiant étranger, vous bénéficierez d'un environnement d'apprentissage stimulant,
                                d'infrastructures modernes et d'un accompagnement personnalisé tout au long de votre séjour.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-4 pt-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                        <ArrowRight className="w-3 h-3 text-primary" />
                                    </div>
                                    <p className="text-sm text-foreground font-medium">Diplômes reconnus internationalement</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                        <ArrowRight className="w-3 h-3 text-primary" />
                                    </div>
                                    <p className="text-sm text-foreground font-medium">Campus moderne et sécurisé</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                        <ArrowRight className="w-3 h-3 text-primary" />
                                    </div>
                                    <p className="text-sm text-foreground font-medium">Vie culturelle riche</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                        <ArrowRight className="w-3 h-3 text-primary" />
                                    </div>
                                    <p className="text-sm text-foreground font-medium">Frais d'études compétitifs</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Communauté Congolaise - C.E.CO.NGO */}
                    <div className="mb-24">
                        <div className="bg-card border-2 border-border overflow-hidden">
                            <div className="md:flex">
                                <div className="md:w-1/2 relative overflow-hidden">
                                    <img src={heroIntl} alt="Étudiants congolais avec le drapeau de la RDC sur le campus" className="w-full h-full object-cover min-h-[320px]" />
                                </div>
                                <div className="md:w-1/2 p-8 md:p-10 flex flex-col justify-center">
                                    <div className="flex items-center gap-4 mb-6">
                                        <img src={logoCecongo} alt="Logo C.E.CO.NGO" className="w-14 h-14 rounded-full object-cover border-2 border-border" />
                                        <div>
                                            <h3 className="text-2xl font-bold text-foreground">C.E.CO.NGO asbl</h3>
                                            <p className="text-sm text-muted-foreground">Cercle des Étudiants Congolais à Ngozi</p>
                                        </div>
                                    </div>
                                    <p className="text-muted-foreground leading-relaxed mb-6">
                                        Originaires de la République Démocratique du Congo, nos étudiants congolais forment l'une des communautés internationales les plus dynamiques de l'UNG. Regroupés au sein du C.E.CO.NGO, ils cultivent l'entraide, la solidarité et le lien avec leur culture tout en s'intégrant pleinement à la vie universitaire burundaise.
                                    </p>
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="bg-primary/5 border border-border p-4">
                                            <div className="text-2xl font-bold text-primary">RDC</div>
                                            <div className="text-xs text-muted-foreground font-medium">Pays d'origine</div>
                                        </div>
                                        <div className="bg-accent/5 border border-border p-4">
                                            <div className="text-2xl font-bold text-accent">30+</div>
                                            <div className="text-xs text-muted-foreground font-medium">Membres actifs</div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {["Solidarité", "Culture", "Entraide", "Intégration"].map((tag) => (
                                            <span key={tag} className="px-3 py-1 bg-primary/10 text-primary border border-border text-xs font-semibold">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Komera / ASU – International Exchange Experience */}
                    <KomeraSection />

                </div>
            </section>
        </div>
    )
}

function ImageCarousel({ images, alt }) {
    const [current, setCurrent] = useState(0)
    const prev = () => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1))
    const next = () => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1))

    return (
        <div className="relative group">
            <div className="aspect-[4/3] overflow-hidden rounded-2xl border-2 border-border bg-muted">
                <img
                    src={images[current]}
                    alt={`${alt} ${current + 1}`}
                    className="w-full h-full object-cover transition-all duration-500"
                />
            </div>
            <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
                <ChevronLeft className="w-5 h-5 text-primary" />
            </button>
            <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
                <ChevronRight className="w-5 h-5 text-primary" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setCurrent(i)}
                        className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-accent w-5" : "bg-white/60"}`}
                    />
                ))}
            </div>
        </div>
    )
}

function KomeraSection() {
    const programs = [
        {
            icon: Languages,
            title: "Échanges Interculturels",
            description: "Chaque été, un groupe d'étudiants espagnols vient à l'Université de Ngozi pour vivre une expérience d'immersion interculturelle avec les étudiants burundais."
        },
        {
            icon: Lightbulb,
            title: "Cours de Développement Personnel",
            description: "Des professeurs de Komera dispensent des formations en anglais sur le développement personnel, le leadership, et les soft skills essentiels pour le monde professionnel."
        },
        {
            icon: HandHeart,
            title: "Service Communautaire",
            description: "Les volontaires participent à la vie communautaire, découvrent le Burundi et contribuent à des projets de coopération au développement dans les régions de Ngozi et Ndava."
        }
    ]

    return (
        <div className="mb-24">
            <div className="text-center mb-12">
                <div className="inline-flex items-center gap-3 mb-4">
                    <div className="h-px w-10 bg-accent" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Coopération Internationale</span>
                    <div className="h-px w-10 bg-accent" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    Komera <span className="text-muted-foreground font-light">× UNG</span>
                </h2>
                <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                    Anciennement <strong>ASU</strong> (Asociación Solidaria Universitaria), <strong>Komera</strong> est une ONG espagnole fondée en 2007 dont le nom signifie
                    « <em>la force d'accomplir de grandes choses</em> » en Kirundi. Fidèle à sa philosophie — <em>« Pequeñas cosas con mucho amor »</em> (de petites choses avec beaucoup d'amour) —
                    Komera envoie chaque été des groupes d'étudiants et de professeurs à l'Université de Ngozi pour des échanges interculturels et des formations enrichissantes.
                </p>
            </div>

            {/* Two-column: Students & Professors */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
                {/* Students Column */}
                <div className="space-y-5">
                    <ImageCarousel images={komeraStudents} alt="Étudiants Komera à l'UNG" />
                    <div className="bg-card border border-border rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                <Users className="w-5 h-5 text-primary" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground">Étudiants Volontaires</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            Des groupes d'étudiants universitaires espagnols participent à un programme d'immersion estival à l'UNG.
                            Durant leur séjour, ils partagent des moments de vie communautaire avec les étudiants burundais, participent à des activités culturelles,
                            et découvrent la richesse du Burundi — une expérience qui transforme aussi bien ceux qui aident que ceux qui accueillent.
                        </p>
                    </div>
                </div>

                {/* Professors Column */}
                <div className="space-y-5">
                    <ImageCarousel images={komeraProfessors} alt="Professeurs Komera à l'UNG" />
                    <div className="bg-card border border-border rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                                <BookOpen className="w-5 h-5 text-accent" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground">Professeurs & Formateurs</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            Un groupe de professeurs et professionnels espagnols anime des cours intensifs en anglais axés sur le développement personnel,
                            les compétences interpersonnelles (soft skills), la communication, et le leadership. Ces formations complètent le cursus académique
                            et préparent les étudiants de l'UNG aux exigences du marché professionnel international.
                        </p>
                    </div>
                </div>
            </div>

            {/* Programs Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
                {programs.map((program, idx) => {
                    const Icon = program.icon
                    return (
                        <div key={idx} className="bg-card p-6 rounded-2xl border border-border hover:border-accent/40 transition-all group text-center">
                            <div className="w-12 h-12 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                <Icon className="w-6 h-6 text-accent" />
                            </div>
                            <h4 className="text-base font-bold text-foreground mb-2">{program.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{program.description}</p>
                        </div>
                    )
                })}
            </div>

            {/* Stats */}
            <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border border-border rounded-2xl p-8 flex items-center justify-center">
                <div className="flex flex-wrap gap-8 justify-center">
                    {[
                        { value: "2007", label: "Fondée en" },
                        { value: "18+", label: "Années de coopération" },
                        { value: "Espagne", label: "Pays d'origine" },
                        { value: "Chaque été", label: "Fréquence des visites" }
                    ].map((stat, idx) => (
                        <div key={idx} className="text-center">
                            <div className="text-xl font-black text-primary">{stat.value}</div>
                            <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
